import { Button } from '@/components/ui/button';
import { Printer, Save, Edit } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ShiftFormData } from './ShiftPrepForm';

type Props = { data: ShiftFormData; onEdit: () => void; onSave: () => void; isSaving: boolean };

export default function ShiftPrepOutput({ data, onEdit, onSave, isSaving }: Props) {
  const dateStr = data.shiftDate ? format(parseISO(data.shiftDate), 'EEEE do MMMM yyyy') : 'Today';

  const checklist = [
    'Condiments filled and topped up',
    'Sauce / squeeze bottles checked',
    'Cutlery polished and rolled',
    'Glassware stocked and polished',
    'Bar fruit / prep ready',
    'Ice checked and topped up',
    'Menus out and up to date',
    'Specials board updated',
    'Toilets checked and stocked',
    'Bookings reviewed',
    'Run sheet printed',
    'Staff brief completed',
    ...(data.firesRequired ? ['Fires checked and lit 🔥'] : []),
    ...(data.musicRequired ? ['Music system on and playlist running 🎵'] : []),
    ...(data.tvRequired ? ['TVs / Sky / TNT set to correct channels 📺'] : []),
    ...(data.outdoorArea ? ['Outdoor area set up and ready 🌿'] : []),
    ...(data.dogsExpected ? ['Dog bowls filled and water ready outside 🐶'] : []),
  ];

  const endOfShift = [
    'Till report printed and checked',
    'Cash counted and dropped',
    'All deliveries logged',
    'Cellar temperatures checked',
    'All areas signed off',
    'Next shift manager briefed',
    'Incident log updated if needed',
    'Building checks complete',
  ];

  return (
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <Button variant="outline" size="sm" onClick={onEdit}><Edit className="w-4 h-4 mr-1.5" /> Edit</Button>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
        <Button size="sm" onClick={onSave} disabled={isSaving}><Save className="w-4 h-4 mr-1.5" />{isSaving ? 'Saving...' : 'Save'}</Button>
      </div>

      <div className="print-page bg-card border border-border rounded-xl p-6 md:p-8 space-y-5 max-w-4xl">
        {/* Header */}
        <div className="border-b-2 border-foreground pb-4">
          <h1 className="text-2xl font-bold tracking-widest uppercase">The Rattlebone</h1>
          <h2 className="text-base font-semibold text-muted-foreground uppercase tracking-wide">Shift Prep Sheet</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Date</span>{dateStr}</div>
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Shift</span>{data.shiftType}</div>
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Manager</span>{data.managerInCharge || '—'}</div>
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Printed</span>{format(new Date(), 'HH:mm dd/MM/yy')}</div>
          </div>
        </div>

        {/* Weather */}
        {data.weatherDescription && (
          <div className="bg-muted/40 rounded-lg p-3">
            <h3 className="font-semibold text-sm mb-1">🌤 Weather: {data.weatherDescription} — {data.weatherTemp}°C</h3>
            {data.weatherAdvice.length > 0 && (
              <ul className="text-sm text-muted-foreground space-y-0.5">{data.weatherAdvice.map((a, i) => <li key={i}>• {a}</li>)}</ul>
            )}
          </div>
        )}

        {/* Trade snapshot */}
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wide border-b border-border pb-1 mb-2">Expected Trading</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><span className="text-muted-foreground">Covers: </span><strong>{data.expectedCovers || '—'}</strong></div>
            <div><span className="text-muted-foreground">Staff: </span><strong>{data.staffCount || '—'}</strong></div>
            <div><span className="text-muted-foreground">First booking: </span><strong>{data.firstBookingTime || '—'}</strong></div>
          </div>
        </div>

        {/* Bookings & events */}
        {(data.eventsToday || data.vips || data.allergens || data.largeTables || data.preOrders) && (
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wide border-b border-border pb-1 mb-2">Bookings & Events</h3>
            <div className="space-y-1 text-sm">
              {data.eventsToday && <p><span className="font-medium">Events:</span> {data.eventsToday}</p>}
              {data.eventTimes && <p><span className="text-muted-foreground">Times:</span> {data.eventTimes}{data.eventLocations ? ` — ${data.eventLocations}` : ''}</p>}
              {data.specialMenus && <p><span className="text-muted-foreground">Special menus:</span> {data.specialMenus}</p>}
              {data.preOrders && <p><span className="text-muted-foreground">Pre-orders:</span> {data.preOrders}</p>}
              {data.vips && <p className="text-amber-600 dark:text-amber-400 font-medium">⭐ VIPs: {data.vips}</p>}
              {data.allergens && <p className="text-destructive font-medium">⚠️ Allergens: {data.allergens}</p>}
              {data.largeTables && <p><span className="text-muted-foreground">Large tables:</span> {data.largeTables}</p>}
            </div>
          </div>
        )}

        {/* Staff plan */}
        {(data.staffArrivals || data.roleAllocations) && (
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wide border-b border-border pb-1 mb-2">Staff Plan</h3>
            <div className="text-sm space-y-1">
              {data.staffArrivals && <p><span className="text-muted-foreground">Arrivals:</span> {data.staffArrivals}</p>}
              {data.roleAllocations && <p><span className="text-muted-foreground">Roles:</span> {data.roleAllocations}</p>}
              {data.staffChanges && <p><span className="text-muted-foreground">Changes:</span> {data.staffChanges}</p>}
            </div>
          </div>
        )}

        {/* Setup checklist */}
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wide border-b border-border pb-1 mb-3">Setup Checklist</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-foreground/40 rounded shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {(data.notes || data.specialSetupNotes) && (
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wide border-b border-border pb-1 mb-2">Manager Notes</h3>
            {data.specialSetupNotes && <p className="text-sm mb-1">{data.specialSetupNotes}</p>}
            {data.notes && <p className="text-sm">{data.notes}</p>}
          </div>
        )}

        {/* End of shift */}
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wide border-b border-border pb-1 mb-3">End of Shift Reminders</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {endOfShift.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-foreground/40 rounded shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sign off */}
        <div className="border-t-2 border-foreground pt-4 grid grid-cols-2 gap-8 text-sm">
          <div><p className="font-medium">Prepared by: _______________________</p><p className="mt-3 text-xs text-muted-foreground">Signature / Time</p></div>
          <div><p className="font-medium">Manager check: _______________________</p><p className="mt-3 text-xs text-muted-foreground">Signature / Time</p></div>
        </div>
      </div>
    </div>
  );
}
